import { FormEvent, useMemo, useState } from 'react'
import { FaCheck, FaCircle, FaEye, FaEyeSlash } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Field, FieldGrid, IconButton, Input, useToast } from 'ui'
import { apiErrorCode, apiErrorMessage, formatDateTime, ReauthBanner, UserProfile } from './shared'
import s from './styles.module.scss'

const PASSWORD_RULES: { id: string; label: string; test: (pwd: string) => boolean }[] = [
    { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { id: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { id: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
    { id: 'number', label: 'A number', test: (p) => /\d/.test(p) },
    { id: 'symbol', label: 'A symbol (e.g. ! @ # $ %)', test: (p) => /[^A-Za-z0-9]/.test(p) },
]

interface Props {
    profile: UserProfile
    onChange: (patch: Partial<UserProfile>) => void
}

/** Change password via Cognito's ChangePassword (verifies the current one). */
export const PasswordCard = ({ profile, onChange }: Props) => {
    const { toast } = useToast()

    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [show, setShow] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reauth, setReauth] = useState(false)

    const ruleResults = useMemo(() => PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(newPassword) })), [newPassword])
    const allRulesMet = ruleResults.every((rule) => rule.met)
    const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword
    const canSubmit = currentPassword.length > 0 && allRulesMet && passwordsMatch && currentPassword !== newPassword && !busy

    const reset = () => {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setShow(false)
    }

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault()
        if (!canSubmit) return
        setError(null)
        setReauth(false)
        setBusy(true)
        try {
            const response = await api({ url: '/profile-change-password', data: { currentPassword, newPassword } })
            if (!response.data?.success) throw new Error(response.data?.error || 'Password change failed')

            reset()
            onChange({ passwordChangedAt: new Date().toISOString() })
            toast({ title: 'Password changed', description: 'Use your new password next time you sign in.', tone: 'good' })
        } catch (err: any) {
            const code = apiErrorCode(err)
            if (code === 'REAUTH_REQUIRED') setReauth(true)
            else setError(apiErrorMessage(err, 'Could not change your password.'))
            if (code === 'WRONG_PASSWORD') setCurrentPassword('')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card
            title="Password"
            subtitle={
                profile.passwordChangedAt
                    ? `Last changed ${formatDateTime(profile.passwordChangedAt)}. Choose a strong password you don't use anywhere else.`
                    : "Choose a strong password you don't use anywhere else."
            }
        >
            <form className={s.form} onSubmit={handleSubmit} noValidate>
                {reauth && <ReauthBanner action="password changes" />}
                {error && (
                    <Banner tone="bad" title="Couldn't change password">
                        {error}
                    </Banner>
                )}
                <FieldGrid minColumn={240}>
                    <Field label="Current password" required span="all">
                        {(id) => (
                            <div className={s.passwordField}>
                                <Input
                                    id={id}
                                    type={show ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    autoComplete="current-password"
                                    disabled={busy}
                                />
                                <IconButton
                                    className={s.passwordToggle}
                                    variant="ghost"
                                    size="sm"
                                    label={show ? 'Hide passwords' : 'Show passwords'}
                                    icon={show ? <FaEyeSlash /> : <FaEye />}
                                    onClick={() => setShow((v) => !v)}
                                />
                            </div>
                        )}
                    </Field>
                    <Field label="New password" required>
                        {(id) => (
                            <Input
                                id={id}
                                type={show ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                invalid={newPassword.length > 0 && !allRulesMet}
                                disabled={busy}
                            />
                        )}
                    </Field>
                    <Field
                        label="Confirm new password"
                        required
                        error={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : undefined}
                    >
                        {(id) => (
                            <Input
                                id={id}
                                type={show ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                invalid={confirmPassword.length > 0 && !passwordsMatch}
                                disabled={busy}
                            />
                        )}
                    </Field>
                </FieldGrid>
                <ul className={s.rules} aria-label="Password requirements">
                    {ruleResults.map((rule) => (
                        <li key={rule.id} className={`${s.rule} ${rule.met ? s.ruleMet : ''}`}>
                            {rule.met ? <FaCheck aria-hidden="true" /> : <FaCircle aria-hidden="true" />}
                            {rule.label}
                        </li>
                    ))}
                    <li className={`${s.rule} ${passwordsMatch ? s.ruleMet : ''}`}>
                        {passwordsMatch ? <FaCheck aria-hidden="true" /> : <FaCircle aria-hidden="true" />}
                        Both new passwords match
                    </li>
                </ul>
                <div className={s.formActions}>
                    {currentPassword && newPassword && currentPassword === newPassword && (
                        <p className={s.formNote}>New password must be different from your current one.</p>
                    )}
                    <Button
                        type="button"
                        onClick={() => {
                            reset()
                            setError(null)
                            setReauth(false)
                        }}
                        disabled={busy || (!currentPassword && !newPassword && !confirmPassword)}
                    >
                        Clear
                    </Button>
                    <Button type="submit" loading={busy} disabled={!canSubmit}>
                        Update password
                    </Button>
                </div>
            </form>
        </Card>
    )
}
